var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { contextValues } from '@streem/logger';
import { invariant } from '@streem/toolbox';
import { UnauthenticatedError } from './errors';
import { AccessTokenManager } from './managers/access_token_manager';
import { OAuthManager, REDIRECT_URI, REFRESH_TOKEN_STORAGE_KEY } from './managers/oauth_manager';
import { isExpertUser, isLoginIdentityUser } from './types/guards';
import { getCompanyCodeFromIdToken, getCompanyUserFromToken, isCompanyUserToken, } from './utils/company_user';
import { getLoginIdentityFromToken } from './utils/identified_user';
import { validJwtToken } from './utils/jwt_token';
import { getInvitedUserFromCredentials } from './utils/invite_user';
import { logger, setLogger } from './utils/logger';
import { getSDKUserFromCredentials } from './utils/sdk_user';
import { observable, autorun } from 'mobx';
class StreemAuth {
    constructor() {
        this.initialized = false;
        this.configCache = new Map();
        this.userLoggedOut = false;
        this.authCallbacks = new Set();
        this.disposers = [];
        this.tokenFetcher = {
            isTokenAvailable: () => {
                return this.authenticated;
            },
            fetchAccessToken: async () => {
                return await this.fetchBearerToken();
            },
            refreshAccessToken: async () => {
                await this.refreshAccessToken();
            },
        };
    }
    configure(config) {
        setLogger(config.logger);
        this.centralLoginAppName = config.centralLoginAppName;
        this.regionalApiUrl = config.regionalApiUrl;
        this.globalApiUrl = config.globalApiUrl;
        this.accessTokenManager = new AccessTokenManager(this.regionalApiUrl, config.appId);
        this.oauthManager = new OAuthManager();
        // Kickoff the async initialization
        logger.info('Kicking off the async initialization of StreemAuth');
        this.initialize();
    }
    // the offlineAccess flag enables long-lived single use refresh tokens
    async loginWithOauthRedirect(companyCode, offlineAccess = true) {
        invariant(this.oauthManager, 'StreemAuth.configure has not been called');
        const config = companyCode === this.centralLoginAppName
            ? await this.getCentralLoginConfig()
            : await this.getLoginConfig(companyCode);
        await this.oauthManager.performAuthorizationRequest(config, offlineAccess);
    }
    async loginWithInviteCode(inviteCode) {
        invariant(this.accessTokenManager, 'StreemAuth.configure has not been called');
        // Returns access token after exchanging with invite code.
        const credentials = await this.accessTokenManager.invitationLogin(inviteCode);
        this._accessToken = credentials.access_token;
        const user = await getInvitedUserFromCredentials(credentials);
        this.setAuthUser(user);
        return user;
    }
    async loginWithSDKToken(sdkToken, companyCode) {
        invariant(this.accessTokenManager, 'StreemAuth.configure has not been called');
        const credentials = await this.accessTokenManager.fetch(sdkToken, companyCode);
        this._accessToken = credentials.access_token;
        const user = await getSDKUserFromCredentials(credentials, companyCode);
        this.setAuthUser(user);
        return user;
    }
    async loginSDKUserWithAccessToken(companyCode) {
        invariant(this.accessTokenManager, 'StreemAuth.configure has not been called');
        const credentials = await this.accessTokenManager.getExistingTokenIfValid();
        invariant(credentials, 'No valid access token found');
        this._accessToken = credentials.access_token;
        const user = await getSDKUserFromCredentials(credentials, companyCode);
        this.setAuthUser(user);
        return user;
    }
    /**
     * Logs the current authUser, if one exists, out.
     *
     * The logout action can take one of two paths depending on whether the company is configured with OAuth or not. If
     * the company is using OAuth, then we clear the id and access tokens before redirecting the browser to the SSO
     * logout URL. Otherwise we trigger any onAuthStateChanged callbacks, providing undefined as the user parameter.
     *
     * @param forceWithCompanyCode Force the logout even if the authUser is undefied by providing a companyCode.
     */
    async logout(forceWithCompanyCode = false) {
        this.clearStorage();
        this.userLoggedOut = true;
        if (isExpertUser(this._authUser) || forceWithCompanyCode !== false) {
            const config = await this.getLoginConfig(isExpertUser(this._authUser) && forceWithCompanyCode === false
                ? this._authUser.companyCode
                : forceWithCompanyCode);
            this.redirectToOAuthLogoutEndpoint(config);
        }
        else if (isLoginIdentityUser(this._authUser)) {
            const config = await this.getCentralLoginConfig();
            this.redirectToOAuthLogoutEndpoint(config);
        }
        else {
            this.resetState();
        }
    }
    /**
     * Call any disposer functions and clear the disposers array.
     */
    cleanupDisposers() {
        this.disposers = this.disposers.filter(disposer => {
            disposer();
            return false;
        });
    }
    /**
     * Register a callback that will be invoked each time the current user state changes. If both callback parameters
     * are undefined, it's safe to assume the user is logged out. If the AnyUser is undefined and StreemAuthError is
     * defined, we encountered an error logging in or out.
     *
     * @param callback A callback that receives an optional AnyUser and/or StreemAuthError.
     */
    onAuthStateChanged(callback) {
        this.authCallbacks.add(callback);
        // NOTE: there is existing code that uses this function but does not call the returned function to do cleanup.
        // some of that existing code is in React hooks that fire back-to-back quickly causing multiple `autorun` subscriptions
        // so we need to ensure we cleanup the disposers to prevent memory leaks. Choosing to handle this internally rather than at every call site.
        this.cleanupDisposers();
        const disposer = autorun(() => {
            if (this.initialized && this.latestAuthResponse) {
                callback(...this.latestAuthResponse);
            }
            else if (this.initialized) {
                callback(this._authUser, undefined);
            }
        });
        this.disposers.push(disposer);
        return () => {
            this.cleanupDisposers();
            return this.authCallbacks.delete(callback);
        };
    }
    /**
     * Returns true if we are able to reliably produce an access or id token for making API requests.
     */
    get authenticated() {
        return (isExpertUser(this._authUser) ||
            this._idToken !== undefined ||
            this._accessToken !== undefined);
    }
    /**
     * Returns either an access token for ExpertUser or id token for InvitedUser or LoginIdentityUser. Will throw an
     * UnauthenticatedError if no user is authenticated. It is recommended to call `authenticated()` prior to calling
     * this method to ensure a token is returned successfully.
     */
    async fetchBearerToken() {
        invariant(this.accessTokenManager, 'StreemAuth.configure has not been called');
        invariant(this.userLoggedOut === false, 'Cannot fetch access token since user has already logged out.');
        const validAccessToken = this.accessTokenManager.getExistingTokenIfValid();
        if (validAccessToken) {
            return validAccessToken.access_token;
        }
        if (this._authUser && !isLoginIdentityUser(this._authUser)) {
            if (validJwtToken(this._idToken)) {
                return (await this.accessTokenManager.fetch(this._idToken, this._authUser.companyCode)).access_token;
            }
            else {
                return (await this.accessTokenManager.refresh(this._authUser.companyCode))
                    .access_token;
            }
        }
        if (!this._idToken) {
            throw new UnauthenticatedError('You must first authenticate before fetching an access token.', 401);
        }
        invariant(isLoginIdentityUser(this._authUser), 'Unexpected state in StreemAuth');
        return this._idToken;
    }
    /**
     * Manually trigger a re-fetch of our platform API credentials, store them, and return the latest access token.
     * This function only works with ExpertUser types, it is the caller's responsibility to check the current user.
     */
    async refreshAccessToken() {
        invariant(this.accessTokenManager, 'StreemAuth.configure has not been called');
        const credentials = await this.accessTokenManager.refresh(this._authUser?.companyCode);
        return credentials.access_token;
    }
    /**
     * Clear the user's auth state and trigger any oauth callbacks
     * Usage is for browser sessions that are open but are not the user's current active session
     * Ie the user logged out from another tab, but we want the app state removal to propagate here too
     */
    completeIframeLogout() {
        this.resetState();
    }
    /**
     * Compares whether a given storage key is the same as the refresh token storage key
     *
     * @param storageKey the key to be compared
     * @returns true or false
     */
    keyMatchesRefreshTokenStorageKey(storageKey) {
        return storageKey === REFRESH_TOKEN_STORAGE_KEY;
    }
    async initialize() {
        invariant(this.oauthManager && this.accessTokenManager, 'StreemAuth.configure has not been called');
        // Bail on non-browsers
        if (typeof window === 'undefined') {
            this.initialized = true;
            return;
        }
        // First check if we have a pending OAuth authorization request.
        if (this.oauthManager.pendingAuthorizationRequest()) {
            logger.info('Found pending OAuth request, completing OAuth flow.');
            try {
                const tokenResponse = await this.oauthManager.completeAuthorizationRequest();
                if (tokenResponse) {
                    await this.setUserFromTokenResponse(tokenResponse);
                    logger.info('Successfully authenticated via OAuth.');
                }
            }
            catch (error) {
                logger.error('Error logging in via OAuth.', error);
                this.resetState(error);
            }
            finally {
                this.initialized = true;
            }
            return;
        }
        // Second check if we have a OAuth refresh token available.
        if (this.oauthManager.refreshTokenAvailable()) {
            logger.info('Found OAuth refresh token, using to fetch id token.');
            try {
                const tokenResponse = await this.oauthManager.authorizeWithRefreshToken();
                if (tokenResponse) {
                    await this.setUserFromTokenResponse(tokenResponse);
                    logger.info('Successfully authenticated with OAuth refresh token.');
                }
            }
            catch (error) {
                logger.error('Error logging in via OAuth refresh token.', error);
                // Refresh token failed, so clear storage and reload the page to reinitialize
                this.clearStorage();
                this.resetState(error);
                window.location.reload();
            }
            finally {
                this.initialized = true;
            }
            return;
        }
        // OauthManager found no token, set the default "logged out" state.
        logger.debug('No id token found, user is not logged in.');
        this.resetState();
        this.initialized = true;
    }
    resetState(error) {
        this.setAuthUser(undefined, error);
        this._idToken = undefined;
        this._accessToken = undefined;
    }
    clearStorage() {
        invariant(this.oauthManager && this.accessTokenManager, 'StreemAuth.configure has not been called');
        this.oauthManager.clearStorage();
        this.accessTokenManager.clearStorage();
    }
    /**
     * Directs OAuth companies to logout from Streem on their side.
     *
     * Implies `this.resetState()` due to the browser refreshing
     */
    redirectToOAuthLogoutEndpoint(config) {
        /* eslint-disable camelcase */
        const loginConfig = config.login_configuration;
        const logoutEndpoint = loginConfig?.logout_endpoint;
        const clientId = loginConfig?.open_id?.client_id;
        if (logoutEndpoint && clientId) {
            const logoutParams = new URLSearchParams({
                client_id: clientId,
                post_logout_redirect_uri: REDIRECT_URI,
            });
            window.location.assign(`${logoutEndpoint}?${logoutParams}`);
        }
        /* eslint-enable camelcase */
    }
    async setUserFromTokenResponse(tokenResponse) {
        invariant(this.accessTokenManager, 'StreemAuth.configure has not been called');
        invariant(tokenResponse.idToken, 'setUserFromTokenResponse called with a response that did not contain an idToken');
        // Set the idToken so we're able to use it internally without broadcasting it.
        this._idToken = tokenResponse.idToken;
        // Use the type of idToken to set the correct user type.
        if (isCompanyUserToken(tokenResponse.idToken)) {
            // only reach out for access token if it's a company user and not an invite token
            const oauthCredentials = await this.accessTokenManager.fetch(tokenResponse.idToken, getCompanyCodeFromIdToken(tokenResponse.idToken));
            const companyUser = getCompanyUserFromToken(tokenResponse.idToken, oauthCredentials);
            this.setAuthUser(companyUser);
        }
        else {
            const loginIdentity = getLoginIdentityFromToken(tokenResponse.idToken);
            this.setAuthUser(loginIdentity);
        }
    }
    /**
     * Set authUser and trigger onAuthStateChanged callbacks
     *
     * @param user Some form of Streem user, either identity, company, or invite.
     * @param error
     */
    setAuthUser(user, error) {
        // User Logging in
        if (user) {
            // resetting to false upon login
            this.userLoggedOut = false;
            logger.setContextValue(contextValues.USER, {
                id: user.id,
                companyCode: isExpertUser(user) ? user.companyCode : undefined,
            });
            logger.info('User logged in', user.id);
        }
        // User Logging out
        if (!user && this._authUser) {
            logger.info('User logged out', this._authUser.id);
            logger.removeContextValue(contextValues.USER);
        }
        this._authUser = user;
        this.authCallbacks.forEach(callback => callback(user, error));
        this.latestAuthResponse = [user, error];
    }
    async getLoginConfig(companyCode) {
        let config = this.configCache.get(companyCode);
        if (!config) {
            const response = await fetch(`${this.globalApiUrl}/v1/companies/${companyCode}/public-login-configuration`);
            config = (await response.json());
            this.configCache.set(companyCode, config);
        }
        return config;
    }
    // specifically get login config for Centralized Login App
    async getCentralLoginConfig() {
        let config = this.centralLoginConfigCache;
        if (!config) {
            const response = await fetch(`${this.globalApiUrl}/v1/central-login/public-login-configuration`);
            config = (await response.json());
            this.centralLoginConfigCache = config;
        }
        return config;
    }
}
__decorate([
    observable
], StreemAuth.prototype, "initialized", void 0);
export class StreemAuthTest extends StreemAuth {
}
// This is a singleton, default export is valid here.
// eslint-disable-next-line import/no-default-export
export default new StreemAuth();
//# sourceMappingURL=streem_auth.js.map